import { Vec3 } from 'playcanvas';

import { Events } from './events';
import { Scene } from './scene';

export type VolugraphRevealUniforms = {
    active: number;
    origin: [number, number, number];
    radius: number;
    feather: number;
    edgeWidth: number;
    edgeLighten: number;
};

type VolugraphRevealCfg = {
    enabled?: boolean;
    durationMs?: number;
    maxSplatCount?: number;
    edgeWidthFrac?: number;
    featherFrac?: number;
    edgeLighten?: number;
};

const DEFAULT_DURATION_MS = 1800;
const DEFAULT_EDGE_FRAC = 0.12;
const DEFAULT_FEATHER_FRAC = 0.04;
const DEFAULT_EDGE_LIGHTEN = 0.3;

const neutralUniforms: VolugraphRevealUniforms = {
    active: 0,
    origin: [0, 0, 0],
    radius: 1e9,
    feather: 0.001,
    edgeWidth: 0.001,
    edgeLighten: 0
};

class VolugraphRevealController {
    private scene: Scene;
    private events: Events;

    private running = false;
    private elapsed = 0;
    private durationSec = 0.9;
    private maxRadius = 1;
    private readonly origin = new Vec3();
    private feather = 0.04;
    private edgeWidth = 0.12;
    private edgeLighten = 0.3;

    constructor(scene: Scene, events: Events) {
        this.scene = scene;
        this.events = events;
    }

    private readWindowConfig(): VolugraphRevealCfg | undefined {
        try {
            const w = window as unknown as { __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphReveal?: VolugraphRevealCfg } };
            return w.__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphReveal;
        } catch {
            return undefined;
        }
    }

    start(): void {
        const cfg = this.readWindowConfig();
        if (cfg && cfg.enabled === false) {
            return;
        }

        this.scene.boundDirty = true;
        const bound = this.scene.bound;
        const he = bound.halfExtents;
        const maxExtent = Math.max(he.x, he.y, he.z, 1e-6);
        this.maxRadius = maxExtent * Math.sqrt(3) * 1.15;

        this.origin.copy(bound.center);

        const durationMs = cfg?.durationMs ?? DEFAULT_DURATION_MS;
        this.durationSec = Math.max(0.05, durationMs / 1000);

        const edgeFrac = cfg?.edgeWidthFrac ?? DEFAULT_EDGE_FRAC;
        const featherFrac = cfg?.featherFrac ?? DEFAULT_FEATHER_FRAC;
        this.edgeWidth = Math.max(1e-4, edgeFrac * this.maxRadius);
        this.feather = Math.max(1e-4, featherFrac * this.maxRadius);
        this.edgeLighten = cfg?.edgeLighten ?? DEFAULT_EDGE_LIGHTEN;

        this.elapsed = 0;
        this.running = true;
        this.scene.forceRender = true;
    }

    cancel(): void {
        if (!this.running) {
            return;
        }
        this.running = false;
        this.scene.forceRender = true;
    }

    update(dt: number): void {
        if (!this.running) {
            return;
        }
        this.elapsed += dt;
        if (this.elapsed >= this.durationSec) {
            this.running = false;
        }
        this.scene.forceRender = true;
    }

    getUniforms(): VolugraphRevealUniforms {
        if (!this.running) {
            return neutralUniforms;
        }
        const t = Math.min(1, this.elapsed / this.durationSec);
        const radius = t * this.maxRadius;
        return {
            active: 1,
            origin: [this.origin.x, this.origin.y, this.origin.z],
            radius,
            feather: this.feather,
            edgeWidth: this.edgeWidth,
            edgeLighten: this.edgeLighten
        };
    }
}

function registerVolugraphReveal(events: Events, scene: Scene): VolugraphRevealController {
    const controller = new VolugraphRevealController(scene, events);

    events.function('volugraph.reveal.uniforms', () => controller.getUniforms());

    events.on('volugraph.reveal.start', () => {
        controller.start();
    });
    events.on('volugraph.reveal.cancel', () => {
        controller.cancel();
    });

    scene.app.on('update', (dt: number) => {
        controller.update(dt);
    });

    return controller;
}

export { VolugraphRevealController, registerVolugraphReveal, neutralUniforms };
